import { useMemo } from 'react';

import clsx from 'clsx';

// Hooks
import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import PropTypes from 'prop-types';

import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
import Icon from '@/components/base/Icon';
// Components
import SelectWithGroupIcon from '@/components/base/Select/SelectWithGroupIcon';
import SelectFilter from '@/components/base/SelectFilter';

const GroupOption = ({ option, selected, active }) => {
    return (
        <div className="flex items-center" data-testid={`test-group-${option?.id}`}>
            {option?.value === 'all' ? (
                <Icon
                    className={clsx('mr-px px-2 text-base', { 'text-icoBlue': active || selected })}
                    icon="group"
                />
            ) : (
                <Avatar
                    profileType={null}
                    size="sm"
                    radius="rounded-m"
                    url={option?.image ?? null}
                    fallback={option?.name?.substring(0, 2)}
                    customColorClass={option?.fallback_style}
                />
            )}
            <div className="flex flex-col overflow-hidden pl-3.5 text-left">
                <span
                    className={clsx('truncate font-GSemiBold text-sm transition-all', {
                        'text-icoBlue': selected || active,
                        'text-icoBlack dark:text-white': !selected && !active
                    })}>
                    {option?.label}
                </span>
            </div>
        </div>
    );
};

GroupOption.propTypes = {
    option: PropTypes.object,
    selected: PropTypes.bool,
    active: PropTypes.bool
};

const SelectGroupWidgetDashboardView = ({
    selectedGroup,
    setSelectedGroup,
    withAllLabel = false,
    noValue = false,
    baseStyle = 'noBorder',
    avatarSize = 'sm',
    sizeStyle = 'default',
    avatarGroupIcon = false,
    buttonClassName
}) => {
    const { t } = useTranslation('dashboard');

    const { activatedSpGroups } = useSPGroups({
        revalidateOnMount: false,
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const groupOptions = useMemo(() => {
        const options = activatedSpGroups.map((group) => {
            return {
                label: group.name,
                value: group.id,
                ...group
            };
        });
        if (withAllLabel) {
            const allOption = {
                label: t('dashboard_select_all_groups'),
                value: 'all'
            };
            return [allOption, ...options];
        }
        return options;
    }, []);

    return (
        <SelectFilter
            selected={
                groupOptions?.filter((group) => group.id === selectedGroup?.id)[0] || selectedGroup
            }
            setSelected={setSelectedGroup}
            noBorder
            noValue={noValue}
            className="min-w-[3.2rem]"
            idInput="select-filter-group-dashboard"
            options={groupOptions}
            button={
                <SelectWithGroupIcon
                    sizeStyle={sizeStyle}
                    avatarSize={avatarSize}
                    baseStyle={baseStyle}
                    groupData={selectedGroup}
                    noValue={noValue}
                    avatarGroupIcon={avatarGroupIcon}
                    className={buttonClassName}
                />
            }>
            <GroupOption />
        </SelectFilter>
    );
};

SelectGroupWidgetDashboardView.propTypes = {
    selectedSP: PropTypes.object,
    setSelectedSP: PropTypes.func,
    withAllLabel: PropTypes.bool,
    noValue: PropTypes.bool,
    baseStyle: PropTypes.string,
    avatarSize: PropTypes.string,
    sizeStyle: PropTypes.string,
    avatarGroupIcon: PropTypes.bool,
    buttonClassName: PropTypes.string
};

export default SelectGroupWidgetDashboardView;
